"use client"

import { app, authentication, FrameContexts } from "@microsoft/teams-js"

import { routes, urlForRoute } from "@/config/routes"

//TODO: Use localStorage to avoid passing the sensitive information via notifySuccess or MessageEvent; instead we send the item key instead of result.
interface AuthPopupMessage {
  status: "success" | "failure"
  result: string
}

/** A generic popup component that handles both MSTeams and browser authentication flows. */
export const openPopupForAuthentication = async ({
  url,
  isInTeams,
  frameContext,
  width = 800,
  height = 600,
}: {
  url: string
  isInTeams: boolean
  frameContext?: FrameContexts
  width?: number
  height?: number
}): Promise<string> => {
  if (frameContext === FrameContexts.authentication) {
    throw new Error(
      "Already in authentication frame context, cannot open another popup"
    )
  }

  // The authentication flow must start and end from the same domain, otherwise success and failure messages won't be returned to the window that initiated the call
  const popupUrl = urlForRoute(routes.popup.out(url))

  if (isInTeams && app.isInitialized()) {
    // MS Teams popup flow
    return await authentication.authenticate({
      url: popupUrl,
      width,
      height,
      // isExternal: openPopupInExternalBrowser,
    })
  }

  // Browser popup flow // TODO: This isn't fully tested and may not work
  return new Promise((resolve, reject) => {
    // Calculate center position for the popup
    const left = window.screenX + (window.outerWidth - width) / 2
    const top = window.screenY + (window.outerHeight - height) / 2
    const windowName = "AuthPopup"

    // Open popup window
    const popup = window.open(
      popupUrl,
      windowName,
      `width=${width},height=${height},left=${left},top=${top}`
    )

    if (!popup || popup.closed) {
      const reason = "Failed to open popup. Please check if popups are blocked."
      reject(new Error(reason))
      return
    }

    // Set up message listener for popup communication
    const messageHandler = (event: MessageEvent<AuthPopupMessage>) => {
      if (event.origin !== window.location.origin) {
        // Verify message origin
        return
      }
      if (
        !event.source ||
        !(event.source instanceof Window) ||
        event.source !== popup
      ) {
        // Verify event origin window name
        return
      }
      const message = event.data
      window.removeEventListener("message", messageHandler)
      clearInterval(popupCheckInterval)
      popup.close()

      if (message.status === "success") {
        resolve(message.result)
      } else {
        reject(new Error(message.result))
      }
    }

    // Add message listener
    window.addEventListener("message", messageHandler)

    // Clean up if popup is closed
    const popupCheckInterval = setInterval(() => {
      try {
        if (popup.closed) {
          clearInterval(popupCheckInterval)
          window.removeEventListener("message", messageHandler)
          reject(new Error("Authentication window was closed"))
          return
        }
      } catch (error) {
        // If we can't access popup.closed due to COOP, assume it's still open
        // The popup will still be able to send us a message when it's done
        console.warn("Unable to check if popup is closed:", error) // eslint-disable-line no-console
      }
    }, 1000)
  })
}

// TODO: To pass any additional data along with result, Open our domain, set localStorage key and then redirect to the external url

export const notifySuccessAfterAuthentication = (
  result: string,
  isInTeams: boolean
) => {
  if (isInTeams && app.isInitialized()) {
    authentication.notifySuccess(result)
  } else if (window.opener) {
    postMessageToOpener("success", result)
  }
}

export const notifyFailureAfterAuthentication = (
  result: string,
  isInTeams: boolean
) => {
  if (isInTeams && app.isInitialized()) {
    authentication.notifyFailure(result)
  } else if (window.opener) {
    postMessageToOpener("failure", result)
  }
}

const postMessageToOpener = (status: "success" | "failure", result: string) => {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
  window.opener.postMessage({ status, result }, window.location.origin)
  // Close this window after a short delay
  setTimeout(() => {
    window.close()
  }, 500)
}
